import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import type { Client } from '@/api/clients/clientsApi.types';
import type { ConsentStatusItem } from '@/api/consents/consentsApi.types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PhoneInput } from '@/components/ui/phoneInput';
import { SafeHtml } from '@/components/common/SafeHtml';
import { ConsentDecisionToggle } from '@/components/common/ConsentDecisionToggle';
import { ConsentBulkActions } from '@/components/common/ConsentBulkActions';
import { CONSENT_DECISION, type ConsentDecision } from '@/constants/consents';
import { OWN_CLIENT_PROFILE_QUERY_KEY, OWN_CONSENTS_QUERY_KEY } from '@/constants/queryKeys';
import {
  useCaptureOwnConsentsMutation,
  useUpdateOwnProfileMutation,
} from '@/hooks/useClientPortal';
import { capitalizeWordsAsYouType } from '@/utils/inputFormatting';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';
import { isBlankOrValidPhoneNumber } from '@/utils/phoneNumbers';
import { ConsentProgressNotice } from './ConsentProgressNotice';

function buildInitialDecisions(
  consents: ConsentStatusItem[]
): Record<string, ConsentDecision | undefined> {
  const start: Record<string, ConsentDecision | undefined> = {};
  consents.forEach(function seed(item) {
    if (item.needsReacceptance === true) {
      start[item.documentId] = undefined;
    } else if (item.decision === CONSENT_DECISION.agreed) {
      start[item.documentId] = CONSENT_DECISION.agreed;
    } else if (
      item.decision === CONSENT_DECISION.declined ||
      item.decision === CONSENT_DECISION.withdrawn
    ) {
      start[item.documentId] = CONSENT_DECISION.declined;
    } else {
      start[item.documentId] = undefined;
    }
  });
  return start;
}

interface OnboardingFormProps {
  profile: Client;
  currentConsents: ConsentStatusItem[];
  onFinished: () => void;
}

export function OnboardingForm({ profile, currentConsents, onFinished }: OnboardingFormProps) {
  const { t } = useTranslation('clients');
  const queryClient = useQueryClient();
  const updateProfile = useUpdateOwnProfileMutation();
  const captureConsents = useCaptureOwnConsentsMutation();

  const [phone, setPhone] = useState(profile.phone === null ? '' : profile.phone);
  const [city, setCity] = useState(profile.city === null ? '' : profile.city);
  const [isPhoneValidationVisible, setIsPhoneValidationVisible] = useState(false);
  const initialDecisions = useMemo(
    function build() {
      return buildInitialDecisions(currentConsents);
    },
    [currentConsents]
  );
  const [decisions, setDecisions] =
    useState<Record<string, ConsentDecision | undefined>>(initialDecisions);

  const declinedRequiredTitles = currentConsents
    .filter(function declinedAndRequired(item) {
      return item.isRequired === true && decisions[item.documentId] === CONSENT_DECISION.declined;
    })
    .map(function toTitle(item) {
      return item.title;
    });
  const answeredConsentCount = currentConsents.filter(function answered(item) {
    return decisions[item.documentId] !== undefined;
  }).length;
  const everyConsentDecided = currentConsents.every(function decided(item) {
    return decisions[item.documentId] !== undefined;
  });
  const isSaving = updateProfile.isPending || captureConsents.isPending;

  function acceptAll() {
    setDecisions(function replaceAll() {
      const next: Record<string, ConsentDecision | undefined> = {};
      currentConsents.forEach(function agree(item) {
        next[item.documentId] = CONSENT_DECISION.agreed;
      });
      return next;
    });
  }

  function declineAll() {
    setDecisions(function replaceAll() {
      const next: Record<string, ConsentDecision | undefined> = {};
      currentConsents.forEach(function decline(item) {
        next[item.documentId] = CONSENT_DECISION.declined;
      });
      return next;
    });
  }

  async function finish() {
    if (isBlankOrValidPhoneNumber(phone) === false) {
      setIsPhoneValidationVisible(true);
      return;
    }
    try {
      await updateProfile.mutateAsync({
        phone: phone.trim().length === 0 ? null : phone.trim(),
        city: city.trim().length === 0 ? null : city.trim(),
      });
      const items = currentConsents.map(function toItem(item) {
        return {
          documentId: item.documentId,
          documentVersionId: item.currentVersionId,
          decision:
            decisions[item.documentId] === CONSENT_DECISION.declined
              ? CONSENT_DECISION.declined
              : CONSENT_DECISION.agreed,
        };
      });
      await captureConsents.mutateAsync(items);
      await queryClient.invalidateQueries({ queryKey: OWN_CONSENTS_QUERY_KEY });
      await queryClient.invalidateQueries({ queryKey: OWN_CLIENT_PROFILE_QUERY_KEY });
      showSuccessNotification(
        declinedRequiredTitles.length === 0
          ? t('portal.onboardingComplete')
          : t('portal.consentGate.choicesSaved')
      );
      onFinished();
    } catch (finishError) {
      const backendMessage = resolveApiErrorMessage(finishError);
      showErrorNotification(backendMessage.length > 0 ? backendMessage : t('portal.saveError'));
    }
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-start">
        <Card padding="md" className="md:col-span-1">
          <h2 className="text-sm font-bold text-zooindigo">{t('portal.profileTitle')}</h2>
          <p className="text-xs text-zoomuted mt-1 mb-4">{t('portal.profileHint')}</p>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="portal-phone">{t('form.phone')}</Label>
              <PhoneInput
                inputId="portal-phone"
                value={phone}
                isValidationVisible={isPhoneValidationVisible}
                onChange={setPhone}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="portal-city">{t('form.city')}</Label>
              <Input
                id="portal-city"
                value={city}
                onChange={function update(changeEvent) {
                  setCity(capitalizeWordsAsYouType(changeEvent.target.value));
                }}
              />
            </div>
          </div>
        </Card>

        <Card padding="md" className="md:col-span-2">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-1">
            <div>
              <h2 className="text-sm font-bold text-zooindigo">{t('portal.consentsTitle')}</h2>
              <p className="text-xs text-zoomuted mt-1">{t('portal.consentsHint')}</p>
            </div>
            <ConsentBulkActions
              onAcceptAll={acceptAll}
              onDeclineAll={declineAll}
              isDisabled={isSaving}
            />
          </div>
          <div className="space-y-4 mt-3">
            {currentConsents.map(function renderConsent(item) {
              const decision = decisions[item.documentId];
              return (
                <div key={item.documentId} className="border border-zooborder rounded-xl p-4">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <h3 className="text-sm font-bold text-zooindigo">{item.title}</h3>
                    <Badge
                      tone={item.isRequired === true ? 'warning' : 'muted'}
                      shape="tag"
                      size="sm"
                      uppercase
                      className="text-[10px] px-1.5 shrink-0"
                    >
                      {item.isRequired === true ? t('portal.required') : t('portal.optional')}
                    </Badge>
                  </div>
                  <SafeHtml html={item.body} className="mb-3" />
                  <ConsentDecisionToggle
                    value={decision === undefined ? null : decision}
                    isDisabled={isSaving}
                    onSelect={function pick(choice) {
                      setDecisions(function update(previous) {
                        return { ...previous, [item.documentId]: choice };
                      });
                    }}
                  />
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      <ConsentProgressNotice
        answeredCount={answeredConsentCount}
        totalCount={currentConsents.length}
        declinedRequiredTitles={declinedRequiredTitles}
      />

      <Button
        size="large"
        className="w-full"
        disabled={everyConsentDecided === false}
        isLoading={isSaving}
        onClick={finish}
      >
        {t('portal.finish')}
      </Button>
    </div>
  );
}
